"use client"

import type React from "react"
import { useState, useEffect } from "react"
import {
  IonPage,
  IonHeader,
  IonToolbar,
  IonTitle,
  IonContent,
  IonLoading,
  IonButtons,
  IonBackButton,
  IonToast,
  IonSegment,
  IonSegmentButton,
  IonLabel,
} from "@ionic/react"
import {
  Box,
  Card,
  CardContent,
  Typography,
  TextField,
  Button,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  IconButton,
  Grid,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  CircularProgress,
  Badge,
  Tooltip,
  TablePagination,
  InputAdornment,
  Stepper,
  Step,
  StepLabel,
  LinearProgress,
} from "@mui/material"
import { styled } from "@mui/material/styles"
import {
  Check,
  ArrowBack,
  Search,
  FilterList,
  Visibility,
  ThumbUp,
  ThumbDown,
  Refresh,
  CalendarMonth,
  CheckCircle,
  Pending,
  Cancel,
  History,
  Warning,
  Edit,
  Save,
  Close,
  Inventory,
  Person,
} from "@mui/icons-material"
import { DatePicker } from "@mui/x-date-pickers/DatePicker"
// import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider"
// import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns"
import { 
  getPendingCMDApprovals, 
  getCMDApprovedOrders, 
  updateCMDApproval,
  getWarehouseStocks,
  updateRequestStatus 
} from "../../api/accounting/accounting"
import { getAllApprovedVendors } from "../../api/vendor.service"
import { timeAgo } from "../../common/utils"

const StyledCard = styled(Card)(({ theme }) => ({
  margin: theme.spacing(2),
  padding: theme.spacing(3),
  borderRadius: theme.shape.borderRadius * 2,
  boxShadow: theme.shadows[3],
  [theme.breakpoints.down("sm")]: {
    margin: theme.spacing(1),
    padding: theme.spacing(2),
  },
}))

const PageContainer = styled(Box)(({ theme }) => ({
  padding: theme.spacing(2),
  [theme.breakpoints.down("sm")]: {
    padding: theme.spacing(1),
  },
}))

// Define types based on your API response
interface RequestItem {
  id: number
  item_id: string
  stock_name: string
  requested_qty: string
  vendor_id: string
  status: string
  audited_qty?: string
  cmd_approved_qty?: string
  supplied_qty?: string
  po_approved_qty?: string | null
  purchase_requisition_id?: string
  notes?: string
  unit_price?: string
  available_quantity?: string
  // For editing
  approved_qty?: string
  editMode?: boolean
}

interface PurchaseOrder {
  id: number
  po_number: string
  notes: string | null
  status: string
  created_by: string
  requested_by_name: string
  created_at: string
  approved_at: string | null
  approved_by: string | null
  cmd_approved_at: string | null
  cmd_approved_by: string | null
  cmd_approval_status: string
  audit_approval_status: string
  branch_id: string
  business_id: string
  items: RequestItem[]
  total_value?: string
}

interface Vendor {
  id: string
  name: string
  contact: string
}

const CMDApprovalPage: React.FC = () => {
  const [activeTab, setActiveTab] = useState<'pending' | 'approved'>('pending')
  const [purchaseOrders, setPurchaseOrders] = useState<PurchaseOrder[]>([])
  const [filteredOrders, setFilteredOrders] = useState<PurchaseOrder[]>([])
  const [selectedOrder, setSelectedOrder] = useState<PurchaseOrder | null>(null)
  const [showDetailDialog, setShowDetailDialog] = useState(false)
  const [showApproveDialog, setShowApproveDialog] = useState(false)
  const [showRejectDialog, setShowRejectDialog] = useState(false)
  const [loading, setLoading] = useState(false)
  const [updating, setUpdating] = useState(false)
  const [showToast, setShowToast] = useState(false)
  const [toastMessage, setToastMessage] = useState('')
  const [toastType, setToastType] = useState<'success' | 'error'>('success')
  const [rejectReason, setRejectReason] = useState('')
  const [approvalNotes, setApprovalNotes] = useState('')
  
  // Filters
  const [filters, setFilters] = useState({
    search: '',
    vendor_id: 'all',
    start_date: null as Date | null,
    end_date: null as Date | null,
  })
  const [page, setPage] = useState(0)
  const [rowsPerPage, setRowsPerPage] = useState(10)
  const [totalCount, setTotalCount] = useState(0)

  const [vendors, setVendors] = useState<Vendor[]>([])
  
  useEffect(() => {
    fetchVendors()
  }, [])

  const fetchVendors = async () => {
    try {
      const response = await getAllApprovedVendors()
      console.log('Vendors Response:', response)
      if (response.data) {
        // Map API response to local interface if needed, or just use as is if matches
        // API usually returns { vendor_id, vendor_name, phone, ... }
        // We map it to { id, name, contact } for this component's existing logic
        const mappedVendors: Vendor[] = response.data.map((v: any) => ({
          id: v.vendor_id,
          name: v.vendor_name,
          contact: v.phone
        }))
        setVendors(mappedVendors)
      }
    } catch (error) {
      console.error("Failed to fetch vendors", error)
      showErrorToast('Failed to fetch vendors')
    }
  }

  const statusOptions = [
    { value: 'pending', label: 'Pending CMD Approval', color: 'warning' },
    { value: 'po_approved', label: 'PO Approved', color: 'info' },
    { value: 'cmd_approved', label: 'CMD Approved', color: 'success' },
    { value: 'rejected', label: 'Rejected', color: 'error' },
    { value: 'draft', label: 'Draft', color: 'default' },
  ]

  useEffect(() => {
    fetchPurchaseOrders()
  }, [activeTab, page, rowsPerPage])

  useEffect(() => {
    applyFilters()
  }, [purchaseOrders, filters])

  const fetchPurchaseOrders = async () => {
    setLoading(true)
    try {
      const params = {
        page: (page + 1).toString(),
        per_page: rowsPerPage.toString(),
        ...(filters.search && { search: filters.search }),
        ...(filters.vendor_id !== 'all' && { vendor_id: filters.vendor_id }),
      }

      let response
      if (activeTab === 'pending') {
        response = await getPendingCMDApprovals(params)
      } else {
        response = await getCMDApprovedOrders(params)
      }

      console.log('CMD Orders Response:', response)

      if (response.data && response.data.data) {
        const orders = response.data.data.map((order: any) => ({
          id: order.id,
          po_number: order.po_number,
          notes: order.notes,
          status: order.status,
          created_by: order.created_by,
          requested_by_name: order.requested_by_name,
          created_at: order.created_at,
          approved_at: order.approved_at,
          approved_by: order.approved_by,
          cmd_approved_at: order.cmd_approved_at,
          cmd_approved_by: order.cmd_approved_by,
          cmd_approval_status: order.cmd_approval_status || 'pending',
          audit_approval_status: order.audit_approval_status || 'pending',
          branch_id: order.branch_id,
          business_id: order.business_id,
          total_value: order.total_value,
          items: order.items.map((item: any) => ({
            id: item.id,
            item_id: item.item_id,
            stock_name: item.stock_name,
            requested_qty: item.requested_qty,
            vendor_id: item.vendor_id,
            status: item.status,
            audited_qty: item.audited_qty || '0',
            cmd_approved_qty: (item.cmd_approved_qty && parseFloat(item.cmd_approved_qty) > 0) ? item.cmd_approved_qty : item.requested_qty,
            supplied_qty: item.supplied_qty || '0',
            po_approved_qty: item.po_approved_qty || item.requested_qty,
            purchase_requisition_id: item.purchase_requisition_id,
            notes: item.notes,
            unit_price: item.unit_price,
            available_quantity: item.available_quantity,
            approved_qty: item.cmd_approved_qty || item.requested_qty,
            editMode: false
          }))
        }))

        setPurchaseOrders(orders)
        setTotalCount(orders.length)
      }
    } catch (error) {
      console.error("Failed to fetch purchase orders", error)
      showErrorToast('Failed to fetch purchase orders')
    } finally {
      setLoading(false)
    }
  }

  const applyFilters = () => {
    let filtered = [...purchaseOrders]

    // Filter by tab (cmd_approval_status)
    if (activeTab === 'pending') {
      filtered = filtered.filter(order => 
        order.cmd_approval_status == 'pending' || 
        !order.cmd_approval_status
      )
    } else if (activeTab === 'approved') {
      filtered = filtered.filter(order => 
        order.cmd_approval_status == 'approved' || 
        order.cmd_approval_status === 'cmd_approved'
      )
    }

    if (filters.search) {
      const query = filters.search.toLowerCase()
      filtered = filtered.filter(order =>
        order.po_number.toLowerCase().includes(query) ||
        order.requested_by_name.toLowerCase().includes(query) ||
        order.items.some(item => item.stock_name.toLowerCase().includes(query))
      )
    }

    if (filters.vendor_id !== 'all') {
      filtered = filtered.filter(order => 
        order.items.some(item => item.vendor_id === filters.vendor_id)
      )
    }

    setFilteredOrders(filtered)
  }

  const showSuccessToast = (message: string) => {
    setToastMessage(message)
    setToastType('success')
    setShowToast(true)
  }

  const showErrorToast = (message: string) => {
    setToastMessage(message)
    setToastType('error')
    setShowToast(true)
  }

  const handleViewOrder = (order: PurchaseOrder) => {
    setSelectedOrder(order)
    setShowDetailDialog(true)
  }

  const handleApproveOrder = (order: PurchaseOrder) => {
    setSelectedOrder(order)
    setShowApproveDialog(true)
  }

  const handleRejectOrder = (order: PurchaseOrder) => {
    setSelectedOrder(order)
    setShowRejectDialog(true)
  }

  const handleUpdateItemQuantity = (orderId: number, itemId: number, field: string, value: string) => {
    setPurchaseOrders(prev => prev.map(order => {
      if (order.id === orderId) {
        return {
          ...order,
          items: order.items.map(item => {
            if (item.id === itemId) {
              return { ...item, [field]: value }
            }
            return item
          })
        }
      }
      return order
    }))

    if (selectedOrder && selectedOrder.id === orderId) {
      setSelectedOrder(prev => {
        if (!prev) return null
        return {
          ...prev,
          items: prev.items.map(item => {
            if (item.id === itemId) {
              return { ...item, [field]: value }
            }
            return item
          })
        }
      })
    }
  }

  const saveOrderChanges = async (orderId?: number) => {
    if (!orderId) return
    const order = purchaseOrders.find(o => o.id === orderId)
    if (!order) return

    setUpdating(true)
    try {
      const payload = {
        purchase_requisition_id: order.po_number,
        action: 'cmd_approve_items',
        items: order.items.map(item => ({
             item_id: item.item_id,
             cmd_approved_qty: item.cmd_approved_qty,
             notes: item.notes
        }))
      }

      console.log('Saving CMD order changes:', payload)
      const response = await updateRequestStatus(payload)
      console.log('Save response:', response)

      if (response && (response.status === 200 || response.status === true || response.data?.status)) {
         showSuccessToast('Order updated successfully')
         setShowDetailDialog(false)
         fetchPurchaseOrders() 
      } else {
         showSuccessToast('Order updated locally (API pending)')
         setShowDetailDialog(false)
      }
    } catch (error) {
      console.error('Error saving order:', error)
      showErrorToast('Failed to save changes')
    } finally {
      setUpdating(false)
    }
  }

  const submitCMDApproval = async (action: 'approve' | 'reject') => {
    if (!selectedOrder) return

    setUpdating(true)
    try {
      const approvalData = {
        purchase_requisition_id: selectedOrder.po_number,
        // action: "cmd_approval_status",
        action: action,
        ...(action === 'approve' && {
          items: selectedOrder.items.map(item => ({
            item_id: item.item_id,
            cmd_approved_qty: item.approved_qty || item.cmd_approved_qty || item.requested_qty,
            notes: approvalNotes
          }))
        }),
        ...(action === 'reject' && {
          rejection_reason: rejectReason
        })
      }

      console.log('Submitting CMD Approval:', approvalData)
      // return
      const response = await updateCMDApproval(approvalData)
      console.log('CMD Approval Response:', response)

      if (response.data && response.data.status) {
        if (action === 'approve') {
          showSuccessToast('Purchase order approved by CMD successfully!')
        } else {
          showSuccessToast('Purchase order rejected successfully!')
        }
        
        setShowApproveDialog(false)
        setShowRejectDialog(false)
        setShowDetailDialog(false)
        setRejectReason('')
        setApprovalNotes('')
        setSelectedOrder(null)
        
        // Refresh the orders list
        await fetchPurchaseOrders()
      } else {
        showErrorToast(response.data?.message || 'Failed to update approval status')
      }
    } catch (error: any) {
      console.error('Error submitting CMD approval:', error)
      showErrorToast(error.response?.data?.message || 'Error submitting approval')
    } finally {
      setUpdating(false)
    }
  }

  const getStatusChip = (status: string) => {
    const statusMap: any = {
      draft: { label: 'Draft', color: 'default', icon: <Pending /> },
      pending: { label: 'Pending CMD', color: 'warning', icon: <Pending /> },
      po_approved: { label: 'PO Approved', color: 'info', icon: <CheckCircle /> },
      cmd_approved: { label: 'CMD Approved', color: 'success', icon: <CheckCircle /> },
      rejected: { label: 'Rejected', color: 'error', icon: <Cancel /> },
      partially_approved: { label: 'Partially Approved', color: 'info', icon: <CheckCircle /> },
    }

    const statusInfo = statusMap[status] || { label: status, color: 'default', icon: null }

    return (
      <Chip
        label={statusInfo.label}
        color={statusInfo.color as any}
        size="small"
        icon={statusInfo.icon}
        variant="outlined"
      />
    )
  }

  const getItemStatusChip = (item: RequestItem) => {
    const statusMap: any = {
      pending: { label: 'Pending', color: 'warning' },
      approved: { label: 'Approved', color: 'success' },
      partially_approved: { label: 'Partial', color: 'info' },
      rejected: { label: 'Rejected', color: 'error' },
    }

    const statusInfo = statusMap[item.status] || { label: item.status, color: 'default' }

    return (
      <Chip
        label={statusInfo.label}
        color={statusInfo.color as any}
        size="small"
      />
    )
  }

  const calculateTotalApprovalRate = (order: PurchaseOrder) => {
    const totalRequested = order.items.reduce((sum, item) => sum + parseFloat(item.requested_qty), 0)
    const totalApproved = order.items.reduce((sum, item) => sum + parseFloat(item.approved_qty || item.cmd_approved_qty || '0'), 0)
    
    if (totalRequested === 0) return 0
    return (totalApproved / totalRequested) * 100
  }

  const handleFilterChange = (key: string, value: any) => {
    setFilters(prev => ({ ...prev, [key]: value }))
    setPage(0)
  }

  const renderPendingTab = () => (
    <>
      <Box
        sx={{
          bgcolor: "#1976d2",
          color: "white",
          py: 3,
          px: 4,
          boxShadow: "0 4px 6px rgba(0, 0, 0, 0.1)",
          mb: 3,
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 2, mb: 1 }}>
          <IconButton sx={{ color: "white" }} onClick={() => window.history.back()}>
            <ArrowBack />
          </IconButton>
          <Person sx={{ fontSize: 32 }} />
          <Typography variant="h4" sx={{ fontWeight: 600 }}>
            CMD Approval Dashboard
          </Typography>
        </Box>
        <Typography variant="body2" sx={{ opacity: 0.9, ml: 7 }}>
          Review and approve purchase orders requiring Chief Medical Director authorization
        </Typography>
      </Box>

      {/* Stats Cards */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} md={3}>
          <Card sx={{ bgcolor: "#fff3e0", borderLeft: "4px solid #ff9800" }}>
            <CardContent>
              <Typography variant="h3" sx={{ fontWeight: 700, color: "#ff9800" }}>
                {purchaseOrders.length}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Pending Orders
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} md={3}>
          <Card sx={{ bgcolor: "#e8f5e9", borderLeft: "4px solid #4caf50" }}>
            <CardContent>
              <Typography variant="h3" sx={{ fontWeight: 700, color: "#4caf50" }}>
                {purchaseOrders.reduce((sum, order) => sum + order.items.length, 0)}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Total Items
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} md={3}>
          <Card sx={{ bgcolor: "#e3f2fd", borderLeft: "4px solid #2196f3" }}>
            <CardContent>
              <Typography variant="h3" sx={{ fontWeight: 700, color: "#2196f3" }}>
                {vendors.length}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Vendors
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} md={3}>
          <Card sx={{ bgcolor: "#f3e5f5", borderLeft: "4px solid #9c27b0" }}>
            <CardContent>
              <Typography variant="h3" sx={{ fontWeight: 700, color: "#9c27b0" }}>
                {purchaseOrders.reduce((sum, order) => {
                  const totalRequested = order.items.reduce((s, item) => s + parseFloat(item.requested_qty), 0)
                  return sum + totalRequested
                }, 0)}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Total Units
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Filters Section */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h6" sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
            <FilterList /> Filters
          </Typography>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                size="small"
                label="Search PO Number or Item"
                value={filters.search}
                onChange={(e) => handleFilterChange('search', e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Search />
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>
            <Grid item xs={12} md={3}>
              <FormControl fullWidth size="small">
                <InputLabel>Vendor</InputLabel>
                <Select
                  value={filters.vendor_id}
                  label="Vendor"
                  onChange={(e) => handleFilterChange('vendor_id', e.target.value)}
                >
                  <MenuItem value="all">All Vendors</MenuItem>
                  {vendors.map((vendor) => (
                    <MenuItem key={vendor.id} value={vendor.id}>
                      {vendor.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={2}>
              <DatePicker
                label="Start Date"
                value={filters.start_date}
                onChange={(newValue) => handleFilterChange('start_date', newValue)}
                slotProps={{ textField: { size: 'small', fullWidth: true } }}
              />
            </Grid>
            <Grid item xs={12} md={2}>
              <DatePicker
                label="End Date"
                value={filters.end_date}
                onChange={(newValue) => handleFilterChange('end_date', newValue)}
                slotProps={{ textField: { size: 'small', fullWidth: true } }}
              />
            </Grid>
            <Grid item xs={12} md={1}>
              <Button
                fullWidth
                variant="outlined"
                onClick={() => setFilters({
                  search: '',
                  vendor_id: 'all',
                  start_date: null,
                  end_date: null,
                })}
                size="small"
              >
                Clear
              </Button>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Purchase Orders Table */}
      <Card>
        <CardContent>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
            <Typography variant="h5">
              Orders Pending CMD Approval ({filteredOrders.length})
            </Typography>
            <Box sx={{ display: 'flex', gap: 1 }}>
              <Button
                variant="outlined"
                startIcon={<Refresh />}
                onClick={fetchPurchaseOrders}
                disabled={loading}
              >
                Refresh
              </Button>
            </Box>
          </Box>

          {loading ? (
            <Box sx={{ py: 4, textAlign: 'center' }}>
              <CircularProgress />
            </Box>
          ) : filteredOrders.length === 0 ? (
            <Alert severity="info" sx={{ textAlign: 'center', py: 3 }}>
              No pending orders requiring CMD approval
            </Alert>
          ) : (
            <>
              <TableContainer>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell><strong>PO Number</strong></TableCell>
                      <TableCell><strong>Requested By</strong></TableCell>
                      <TableCell><strong>Date</strong></TableCell>
                      <TableCell><strong>Items</strong></TableCell>
                      <TableCell><strong>Approval Rate</strong></TableCell>
                      <TableCell><strong>Status</strong></TableCell>
                      <TableCell><strong>Actions</strong></TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {filteredOrders.map((order) => {
                      const approvalRate = calculateTotalApprovalRate(order)
                      return (
                        <TableRow key={order.id} hover>
                          <TableCell>
                            <Typography variant="body2" fontWeight="bold">
                              {order.po_number}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Typography>{order.requested_by_name}</Typography>
                          </TableCell>
                          <TableCell>
                            {new Date(order.created_at).toLocaleDateString()}
                          </TableCell>
                          <TableCell>
                            <Typography>{order.items.length} items</Typography>
                          </TableCell>
                          <TableCell>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              <Box sx={{ width: '100%', mr: 1 }}>
                                <LinearProgress 
                                  variant="determinate" 
                                  value={approvalRate} 
                                  color={approvalRate === 100 ? "success" : "warning"}
                                />
                              </Box>
                              <Typography variant="body2">
                                {approvalRate.toFixed(0)}%
                              </Typography>
                            </Box>
                          </TableCell>
                          <TableCell>
                            {getStatusChip(order.status)}
                          </TableCell>
                          <TableCell>
                            <Box sx={{ display: 'flex', gap: 1 }}>
                              <Tooltip title="View Details">
                                <IconButton
                                  size="small"
                                  color="info"
                                  onClick={() => handleViewOrder(order)}
                                >
                                  <Visibility />
                                </IconButton>
                              </Tooltip>
                              <Tooltip title="Approve Order">
                                <IconButton
                                  size="small"
                                  color="success"
                                  onClick={() => handleApproveOrder(order)}
                                >
                                  <ThumbUp />
                                </IconButton>
                              </Tooltip>
                              <Tooltip title="Reject Order">
                                <IconButton
                                  size="small"
                                  color="error"
                                  onClick={() => handleRejectOrder(order)}
                                >
                                  <ThumbDown />
                                </IconButton>
                              </Tooltip>
                            </Box>
                          </TableCell>
                        </TableRow>
                      )
                    })}
                  </TableBody>
                </Table>
              </TableContainer>

              <TablePagination
                rowsPerPageOptions={[5, 10, 25, 50]}
                component="div"
                count={totalCount}
                rowsPerPage={rowsPerPage}
                page={page}
                onPageChange={(_, newPage) => setPage(newPage)}
                onRowsPerPageChange={(e) => {
                  setRowsPerPage(parseInt(e.target.value, 10))
                  setPage(0)
                }}
              />
            </>
          )}
        </CardContent>
      </Card>
    </>
  )

  const renderApprovedTab = () => (
    <>
      {/* Stats Cards */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={12} md={3}>
          <Card sx={{ bgcolor: '#e7f5ff' }}>
            <CardContent>
              <Typography variant="h4" sx={{ fontWeight: 700, color: '#0066cc' }}>
                {filteredOrders.length}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Approved Orders
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} md={3}>
          <Card sx={{ bgcolor: '#e6fcf5' }}>
            <CardContent>
              <Typography variant="h4" sx={{ fontWeight: 700, color: '#00a76f' }}>
                ₦{filteredOrders.reduce((sum, order) => sum + Number(order.total_value || 0), 0).toLocaleString()}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Total Value
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} md={3}>
          <Card sx={{ bgcolor: '#fff7e6' }}>
            <CardContent>
              <Typography variant="h4" sx={{ fontWeight: 700, color: '#ff9800' }}>
                {filteredOrders.reduce((sum, order) => sum + order.items.length, 0)}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Total Items
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} md={3}>
          <Card sx={{ bgcolor: '#f3e5f5' }}>
            <CardContent>
              <Typography variant="h4" sx={{ fontWeight: 700, color: '#9c27b0' }}>
                {filteredOrders.reduce((sum, order) => sum + order.items.reduce((itemSum, item) => {
                  return itemSum + Number(item.cmd_approved_qty || 0)
                }, 0), 0)}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Total Units
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Filters Section */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h6" sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
            <FilterList /> Filters
          </Typography>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                size="small"
                label="Search PO Number or Item"
                value={filters.search}
                onChange={(e) => handleFilterChange('search', e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Search />
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>
            <Grid item xs={12} md={3}>
              <FormControl fullWidth size="small">
                <InputLabel>Vendor</InputLabel>
                <Select
                  value={filters.vendor_id}
                  label="Vendor"
                  onChange={(e) => handleFilterChange('vendor_id', e.target.value)}
                >
                  <MenuItem value="all">All Vendors</MenuItem>
                  {vendors.map((vendor) => (
                    <MenuItem key={vendor.id} value={vendor.id}>
                      {vendor.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={2}>
              <DatePicker
                label="Start Date"
                value={filters.start_date}
                onChange={(newValue) => handleFilterChange('start_date', newValue)}
                slotProps={{ textField: { size: 'small', fullWidth: true } }}
              />
            </Grid>
            <Grid item xs={12} md={2}>
              <DatePicker
                label="End Date"
                value={filters.end_date}
                onChange={(newValue) => handleFilterChange('end_date', newValue)}
                slotProps={{ textField: { size: 'small', fullWidth: true } }}
              />
            </Grid>
            <Grid item xs={12} md={1}>
              <Button
                fullWidth
                variant="outlined"
                onClick={() => setFilters({
                  search: '',
                  vendor_id: 'all',
                  start_date: null,
                  end_date: null,
                })}
                size="small"
              >
                Clear
              </Button>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Approved Orders Table */}
      <Card>
        <CardContent>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
            <Typography variant="h5">
              CMD Approved Orders ({filteredOrders.length})
            </Typography>
            <Box sx={{ display: 'flex', gap: 1 }}>
              <Button
                variant="outlined"
                startIcon={<Refresh />}
                onClick={fetchPurchaseOrders}
                disabled={loading}
              >
                Refresh
              </Button>
            </Box>
          </Box>

          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
              <CircularProgress />
            </Box>
          ) : (
            <>
              <TableContainer component={Paper} variant="outlined">
                <Table>
                  <TableHead>
                    <TableRow sx={{ bgcolor: '#f8fafc' }}>
                      <TableCell sx={{ fontWeight: 600 }}>PO Number</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>Requested By</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>Items</TableCell>
                      <TableCell sx={{ fontWeight: 600 }} align="right">Total Value</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>Approved Date</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>Status</TableCell>
                      <TableCell sx={{ fontWeight: 600 }} align="center">Actions</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {filteredOrders.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={7} align="center" sx={{ py: 4 }}>
                          <Typography color="text.secondary">
                            No approved orders found
                          </Typography>
                        </TableCell>
                      </TableRow>
                    ) : (
                      filteredOrders.map((order) => (
                        <TableRow key={order.id} hover>
                          <TableCell>
                            <Typography variant="body2" fontWeight={600}>
                              {order.po_number}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2">
                              {order.requested_by_name}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              {timeAgo(order.created_at)}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Chip label={order.items.length} size="small" variant="outlined" />
                          </TableCell>
                          <TableCell align="right">
                            <Typography variant="body2" fontWeight={600}>
                              ₦{Number(order.total_value || 0).toLocaleString()}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Typography variant="caption" color="text.secondary">
                              {order.cmd_approved_at ? timeAgo(order.cmd_approved_at) : 'N/A'}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Chip
                              label="Approved"
                              color="success"
                              size="small"
                              icon={<CheckCircle />}
                            />
                          </TableCell>
                          <TableCell align="center">
                            <Button
                              size="small"
                              variant="outlined"
                              startIcon={<Visibility />}
                              onClick={() => handleViewOrder(order)}
                            >
                              View
                            </Button>
                          </TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </TableContainer>

              <TablePagination
                rowsPerPageOptions={[5, 10, 25, 50]}
                component="div"
                count={totalCount}
                rowsPerPage={rowsPerPage}
                page={page}
                onPageChange={(_, newPage) => setPage(newPage)}
                onRowsPerPageChange={(e) => {
                  setRowsPerPage(parseInt(e.target.value, 10))
                  setPage(0)
                }}
              />
            </>
          )}
        </CardContent>
      </Card>
    </>
  )

  return (
    <IonPage>
      <IonHeader>
        <IonToolbar>
          <IonButtons slot="start">
            <IonBackButton defaultHref="/dashboard" />
          </IonButtons>
          <IonTitle>CMD Approval System</IonTitle>
        </IonToolbar>
        <IonToolbar>
          <IonSegment
            value={activeTab}
            onIonChange={(e) => setActiveTab(e.detail.value as any)}
            style={{ width: '100%' }}
          >
            <IonSegmentButton value="pending">
              <IonLabel>
                <Badge badgeContent={purchaseOrders.length} color="error">
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Pending fontSize="small" />
                    Pending Approval
                  </Box>
                </Badge>
              </IonLabel>
            </IonSegmentButton>
            <IonSegmentButton value="approved">
              <IonLabel>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <CheckCircle fontSize="small" />
                  Approved Orders
                </Box>
              </IonLabel>
            </IonSegmentButton>
          </IonSegment>
        </IonToolbar>
      </IonHeader>

      <IonContent>
        {/* <LocalizationProvider dateAdapter={AdapterDateFns}> */}
          <PageContainer>
            {activeTab === 'pending' ? renderPendingTab() : renderApprovedTab()}
          </PageContainer>
        {/* </LocalizationProvider> */}

        {/* Order Detail Dialog */}
        <Dialog
          open={showDetailDialog}
          onClose={() => setShowDetailDialog(false)}
          maxWidth="lg"
          fullWidth
          scroll="paper"
        >
          <DialogTitle>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <Typography variant="h6">
                Purchase Order: {selectedOrder?.po_number}
              </Typography>
              {selectedOrder && (
                <Box sx={{ display: 'flex', gap: 2 }}>
                  {getStatusChip(selectedOrder.status)}
                  {(selectedOrder.cmd_approval_status === 'pending' || !selectedOrder.cmd_approval_status) && (
                    <>
                      <Button
                        variant="contained"
                        color="error"
                        size="small"
                        startIcon={<ThumbDown />}
                        onClick={() => handleRejectOrder(selectedOrder)}
                      >
                        Reject
                      </Button>
                      <Button
                        variant="contained"
                        color="success"
                        size="small"
                        startIcon={<ThumbUp />}
                        onClick={() => handleApproveOrder(selectedOrder)}
                      >
                        Approve
                      </Button>
                    </>
                  )}
                </Box>
              )}
            </Box>
          </DialogTitle>
          <DialogContent dividers>
            {selectedOrder && (
              <>
                <Grid container spacing={3} sx={{ mb: 3 }}>
                  <Grid item xs={12} md={4}>
                    <Card sx={{ p: 2 }}>
                      <Typography variant="subtitle2" color="text.secondary">
                        Created By
                      </Typography>
                      <Typography variant="body1" fontWeight="medium">
                        {selectedOrder.requested_by_name}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {new Date(selectedOrder.created_at).toLocaleString()}
                      </Typography>
                    </Card>
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <Card sx={{ p: 2 }}>
                      <Typography variant="subtitle2" color="text.secondary">
                        PO Approval
                      </Typography>
                      {selectedOrder.approved_by ? (
                        <>
                          <Typography variant="body1" fontWeight="medium">
                            Approved by: {selectedOrder.approved_by}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {selectedOrder.approved_at && new Date(selectedOrder.approved_at).toLocaleDateString()}
                          </Typography>
                        </>
                      ) : (
                        <Typography variant="body2" color="text.secondary">
                          Awaiting PO approval
                        </Typography>
                      )}
                    </Card>
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <Card sx={{ p: 2 }}>
                      <Typography variant="subtitle2" color="text.secondary">
                        Summary
                      </Typography>
                      <Typography variant="h6" color="primary" fontWeight="bold">
                        {selectedOrder.items.length} Items
                      </Typography>
                      <Typography variant="caption" display="block">
                        Total requested: {selectedOrder.items.reduce((sum, item) => sum + parseFloat(item.requested_qty), 0)} units
                      </Typography>
                    </Card>
                  </Grid>
                </Grid>

                {/* Workflow Stepper */}
                <Typography variant="h6" gutterBottom>
                  Approval Workflow
                </Typography>
                <Stepper alternativeLabel sx={{ mb: 4 }}>
                  <Step completed={!!selectedOrder.approved_by}>
                    <StepLabel>
                      PO Approval
                      {selectedOrder.approved_by && (
                        <Typography variant="caption" display="block">
                          by {selectedOrder.approved_by}
                        </Typography>
                      )}
                    </StepLabel>
                  </Step>
                  <Step active={!selectedOrder.cmd_approved_by}>
                    <StepLabel>
                      CMD Approval
                      {selectedOrder.cmd_approved_by && (
                        <Typography variant="caption" display="block">
                          by {selectedOrder.cmd_approved_by}
                        </Typography>
                      )}
                    </StepLabel>
                  </Step>
                  <Step>
                    <StepLabel>Vendor Processing</StepLabel>
                  </Step>
                  <Step>
                    <StepLabel>Audit Review</StepLabel>
                  </Step>
                </Stepper>

                {/* Items Table with Quantity Adjustment */}
                <Typography variant="h6" gutterBottom>
                  Items - Adjust Approval Quantities
                </Typography>
                <Alert severity="info" sx={{ mb: 2 }}>
                  You can adjust the approved quantity for each item. The approved quantity cannot exceed the requested quantity.
                </Alert>

                <TableContainer component={Paper}>
                  <Table>
                    <TableHead>
                      <TableRow sx={{ backgroundColor: 'action.hover' }}>
                        <TableCell><strong>Item</strong></TableCell>
                        <TableCell><strong>Vendor</strong></TableCell>
                        <TableCell align="right"><strong>Requested Qty</strong></TableCell>
                        <TableCell align="right"><strong>PO Approved</strong></TableCell>
                        <TableCell align="right"><strong>CMD Approved</strong></TableCell>
                        <TableCell><strong>Status</strong></TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {selectedOrder.items.map((item) => {
                        const vendor = vendors.find(v => v.id === item.vendor_id)
                        const isApproved = parseFloat(item.approved_qty || item.cmd_approved_qty || '0') > 0
                        
                        return (
                          <TableRow key={item.id}>
                            <TableCell>
                              <Typography variant="body2" fontWeight="medium">
                                {item.stock_name}
                              </Typography>
                              <Typography variant="caption" color="text.secondary">
                                ID: {item.item_id}
                              </Typography>
                            </TableCell>
                            <TableCell>
                              <Typography variant="body2">
                                {vendor?.name || item.vendor_id}
                              </Typography>
                            </TableCell>
                            <TableCell align="right">
                              <Typography variant="body2">
                                {item.po_approved_qty}
                              </Typography>
                            </TableCell>
                            <TableCell align="right">
                              <Typography variant="body2" color="info.main">
                                {item.cmd_approved_qty || '0'}
                              </Typography>
                            </TableCell>
                            <TableCell align="right">
                              {/* Always show input if status allows editing */}
                              <TextField
                                  type="number"
                                  size="small"
                                  value={item.cmd_approved_qty}
                                  onChange={(e) => handleUpdateItemQuantity(
                                    selectedOrder.id, 
                                    item.id, 
                                    'cmd_approved_qty', 
                                    e.target.value
                                  )}
                                  inputProps={{ 
                                    min: 0,
                                    max: parseFloat(item.requested_qty),
                                    step: 1
                                  }}
                                  disabled={selectedOrder.cmd_approval_status === 'approved' || selectedOrder.cmd_approval_status === 'cmd_approved'}
                                  sx={{ width: 100 }}
                                />
                            </TableCell>
                            <TableCell>
                              {getItemStatusChip(item)}
                            </TableCell>
                          </TableRow>
                        )
                      })}
                    </TableBody>
                  </Table>
                </TableContainer>

                {selectedOrder.notes && (
                  <Box sx={{ mt: 3 }}>
                    <Typography variant="subtitle2" gutterBottom>
                      Notes:
                    </Typography>
                    <Alert severity="info">
                      {selectedOrder.notes}
                    </Alert>
                  </Box>
                )}
              </>
            )}
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setShowDetailDialog(false)}>Close</Button>
            <Button 
                variant="contained" 
                color="primary" 
                onClick={() => saveOrderChanges(selectedOrder?.id)}
                disabled={updating || selectedOrder?.cmd_approval_status === 'approved' || selectedOrder?.cmd_approval_status === 'cmd_approved'}
            >
              {updating ? <CircularProgress size={24} color="inherit" /> : 'Save Changes'}
            </Button>
          </DialogActions>
        </Dialog>

        {/* Approve Confirmation Dialog */}
        <Dialog
          open={showApproveDialog}
          onClose={() => setShowApproveDialog(false)}
          maxWidth="sm"
          fullWidth
        >
          <DialogTitle sx={{ bgcolor: "#4caf50", color: "white" }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <ThumbUp /> Confirm CMD Approval
            </Box>
          </DialogTitle>
          <DialogContent sx={{ mt: 3 }}>
            {selectedOrder && (
              <>
                <Alert severity="warning" sx={{ mb: 2 }}>
                  You are about to approve this purchase order. Please review the approval quantities.
                </Alert>

                <Box sx={{ mb: 3 }}>
                  <Typography variant="subtitle2" gutterBottom>
                    Purchase Order Summary:
                  </Typography>
                  <Typography variant="body2">
                    • PO Number: {selectedOrder.po_number}
                  </Typography>
                  <Typography variant="body2">
                    • Items: {selectedOrder.items.length}
                  </Typography>
                  <Typography variant="body2">
                    • Total Requested: {selectedOrder.items.reduce((sum, item) => sum + parseFloat(item.requested_qty), 0)} units
                  </Typography>
                  <Typography variant="body2">
                    • Total Approved: {selectedOrder.items.reduce((sum, item) => sum + parseFloat(item.approved_qty || item.cmd_approved_qty || '0'), 0)} units
                  </Typography>
                </Box>

                <TextField
                  fullWidth
                  multiline
                  rows={3}
                  label="Approval Notes (Optional)"
                  value={approvalNotes}
                  onChange={(e) => setApprovalNotes(e.target.value)}
                  placeholder="Add any notes for this approval..."
                  sx={{ mt: 2 }}
                />
              </>
            )}
          </DialogContent>
          <DialogActions sx={{ p: 3 }}>
            <Button onClick={() => setShowApproveDialog(false)} disabled={updating}>
              Cancel
            </Button>
            <Button
              onClick={() => submitCMDApproval('approve')}
              variant="contained"
              color="success"
              disabled={updating}
              startIcon={updating ? <CircularProgress size={20} /> : <Check />}
            >
              {updating ? "Approving..." : "Confirm Approval"}
            </Button>
          </DialogActions>
        </Dialog>

        {/* Reject Confirmation Dialog */}
        <Dialog
          open={showRejectDialog}
          onClose={() => setShowRejectDialog(false)}
          maxWidth="sm"
          fullWidth
        >
          <DialogTitle sx={{ bgcolor: "#f44336", color: "white" }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <ThumbDown /> Confirm Rejection
            </Box>
          </DialogTitle>
          <DialogContent sx={{ mt: 3 }}>
            {selectedOrder && (
              <>
                <Alert severity="error" sx={{ mb: 2 }}>
                  You are about to reject this purchase order. This action cannot be undone.
                </Alert>

                <Box sx={{ mb: 3 }}>
                  <Typography variant="subtitle2" gutterBottom>
                    Purchase Order Details:
                  </Typography>
                  <Typography variant="body2">
                    • PO Number: {selectedOrder.po_number}
                  </Typography>
                  <Typography variant="body2">
                    • Requested By: {selectedOrder.requested_by_name}
                  </Typography>
                  <Typography variant="body2">
                    • Items: {selectedOrder.items.length}
                  </Typography>
                </Box>

                <TextField
                  fullWidth
                  multiline
                  rows={3}
                  label="Rejection Reason *"
                  value={rejectReason}
                  onChange={(e) => setRejectReason(e.target.value)}
                  placeholder="Please provide a reason for rejecting this purchase order..."
                  required
                  error={!rejectReason}
                  helperText={!rejectReason ? "Rejection reason is required" : ""}
                  sx={{ mt: 2 }}
                />
              </>
            )}
          </DialogContent>
          <DialogActions sx={{ p: 3 }}>
            <Button onClick={() => setShowRejectDialog(false)} disabled={updating}>
              Cancel
            </Button>
            <Button
              onClick={() => submitCMDApproval('reject')}
              variant="contained"
              color="error"
              disabled={updating || !rejectReason}
              startIcon={updating ? <CircularProgress size={20} /> : <ThumbDown />}
            >
              {updating ? "Rejecting..." : "Confirm Rejection"}
            </Button>
          </DialogActions>
        </Dialog>

        <IonLoading isOpen={loading || updating} message={"Processing..."} />
        <IonToast
          isOpen={showToast}
          onDidDismiss={() => setShowToast(false)}
          message={toastMessage}
          duration={3000}
          color={toastType}
        />
      </IonContent>
    </IonPage>
  )
}

export default CMDApprovalPage